/**
 * The Mash and Fermentation tabs (PLAN-PROFILES P3).
 *
 * The layout, selection, rename and delete are `ProfileLibraryTab`'s. This file
 * supplies what is particular to a schedule: the step table, and for a mash,
 * what it does to the beer's fermentability. One component serves both tabs
 * because the two libraries differ in nothing a reader of this file would care
 * about. See `document/schedule-profile.ts` for why the *commands* are longhand
 * and this is not: there are no literal command types to satisfy here and no
 * union to narrow, so the shared version costs nothing and drifts nowhere.
 *
 * This tab exists so the vocabulary can be **read**: what "Full body" means,
 * what it will do to a beer, and what the difference between the three
 * decoction schedules actually is.
 */

import type { FermentationProfileDoc, MashProfileDoc } from '@beerwright/commands';
import { toMashStep } from '@beerwright/commands';
import { bodyAttenuationShift, effectiveMashTempC } from '@beerwright/core';
import { ProfileLibraryTab } from '@/components/profiles/profile-library-tab';
import { formatPressureWithUnit } from '@/lib/carbonation';
import { formatNumber } from '@/lib/format';
import { formatQuantity, unitLabel } from '@/lib/units';
import { useUnits } from '@/store/workspace-context';

/** Either library's document, narrowed to what this component reads. */
type ScheduleProfile = MashProfileDoc | FermentationProfileDoc;

/** A mash step and a fermentation step, in the shape the table prints. */
interface StepRow {
  /** The step's own id — stable on a built-in, so it beats the array index. */
  readonly stepId: string;
  readonly name: string;
  readonly detail: string;
  readonly duration: string;
}

export interface ScheduleProfilesTabProps {
  readonly profiles: readonly ScheduleProfile[];
  /** `mashProfile` or `fermentationProfile` — the command family to dispatch. */
  readonly domain: 'mashProfile' | 'fermentationProfile';
  /** How a sentence names one of these, e.g. `mash schedule`. */
  readonly noun: string;
  readonly emptyMessage: string;
}

export function ScheduleProfilesTab({
  profiles,
  domain,
  noun,
  emptyMessage,
}: ScheduleProfilesTabProps) {
  const units = useUnits();
  return (
    <ProfileLibraryTab
      profiles={profiles}
      domain={domain}
      noun={noun}
      contents="steps"
      emptyMessage={emptyMessage}
      summary={(profile) => `${profile.steps.length} steps`}
      detail={(selected) => (
        <>
          <BodyNote profile={selected} units={units} />

          <ol className="divide-y divide-border overflow-hidden rounded-lg border border-border">
            {toRows(selected, units).map((row) => (
              <li
                key={row.stepId}
                className="flex items-baseline justify-between gap-3 px-3 py-2 text-sm"
              >
                <span>{row.name}</span>
                <span className="flex gap-3 font-mono text-muted-foreground text-xs tabular-nums">
                  <span>{row.detail}</span>
                  <span>{row.duration}</span>
                </span>
              </li>
            ))}
          </ol>

          {selected.builtin && (
            <p className="text-muted-foreground text-xs">
              This is a built-in schedule, so you can't change it. Apply it to a recipe, adjust it
              there, and save it back as your own.
            </p>
          )}
        </>
      )}
    />
  );
}

/**
 * What a mash profile does to the beer's fermentability.
 *
 * Only shown on a mash profile, and only when the engine can read a
 * saccharification temperature off it at all.
 *
 * **It asks the engine rather than reading a step.** The first version picked
 * the first rest between 60 and 72 °C and reported that, which is right for a
 * single infusion and wrong for everything else: `effectiveMashTempC` collapses
 * a schedule to the *duration-weighted mean* of its saccharification rests and
 * ignores anything outside the band. On the hefeweizen schedule the naive
 * version would have reported the 62 °C maltose rest and ignored the 72 °C one
 * it is followed by, promising a much drier beer than the prediction makes.
 * Using the engine's own function means this sentence and the vitals rail
 * cannot disagree.
 */
function BodyNote({
  profile,
  units,
}: {
  readonly profile: ScheduleProfile;
  readonly units: ReturnType<typeof useUnits>;
}) {
  const steps = profile.steps;
  if (!steps.every((step) => 'targetTempC' in step)) return null;

  const effective = effectiveMashTempC((steps as MashProfileDoc['steps']).map(toMashStep));
  if (effective === undefined) return null;

  const shift = bodyAttenuationShift(effective);
  const temp = `${formatQuantity(effective, 'temperature', units)} ${unitLabel('temperature', units)}`;
  const rests = steps.length === 1 ? 'Resting at' : 'Its rests average';

  if (Math.abs(shift) < 0.05) {
    return (
      <p className="text-muted-foreground text-sm">
        {rests} {temp}, which is the temperature our attenuation estimate assumes, so it doesn't
        change the prediction.
      </p>
    );
  }
  return (
    <p className="text-muted-foreground text-sm">
      {rests} {temp}, so we predict about <strong>{formatNumber(Math.abs(shift), 1)} points</strong>{' '}
      {shift > 0 ? 'more' : 'less'} apparent attenuation than a mash in the middle of the range.
    </p>
  );
}

/** Both step shapes, flattened into what the table prints. */
function toRows(profile: ScheduleProfile, units: ReturnType<typeof useUnits>): readonly StepRow[] {
  return profile.steps.map((step) => {
    if ('targetTempC' in step) {
      return {
        stepId: step.stepId,
        name: step.name,
        detail: `${formatQuantity(step.targetTempC, 'temperature', units)} ${unitLabel('temperature', units)}`,
        duration: `${step.timeMinutes} min`,
      };
    }
    // The pressure joins the temperature rather than taking a column of its
    // own: it is a *property of that hold*, and one spunded schedule out of
    // eleven would otherwise buy a column that is empty on almost every row.
    const pressure =
      step.pressurePsi === null
        ? ''
        : ` at ${formatPressureWithUnit(step.pressurePsi, units.pressure)}`;
    return {
      stepId: step.stepId,
      name: step.name,
      detail: `${formatQuantity(step.tempC, 'temperature', units)} ${unitLabel('temperature', units)}${pressure}`,
      duration: `${step.days} days`,
    };
  });
}
